import React, { useState, useEffect } from "react";
import { Label, Icon } from "semantic-ui-react";
import { size, filter } from "lodash";
import classNames from "classnames";
import { Link, useHistory } from "react-router-dom";
import { getOrdersByTableApi } from "../../../../api/orders";
import { ORDER_STATUS, getTableKind } from "../../../../utils/constants";
import { getTablePlace, getTableShortName } from "../../../../utils/table";
import { ReactComponent as IcTable } from "../../../../assets/table.svg";
import { usePayment, useAuth, useTable } from "../../../../hooks";
import { cleanProductCartApi } from "../../../../api/cart";
import { isBatchKind, countWaitingForArea } from "../../../../utils/order";
import { GuestsModal } from "../GuestsModal";
import { DeliveryModal } from "../DeliveryModal";
import "./TableAdmin.scss";

export function TableAdmin(props) {
  // onStatus: avisa a la lista si la mesa está ocupada o tiene platos listos
  // (contadores de las pestañas por ambiente en Pedidos).
  const { table, reload, onStatus } = props;
  const [orders, setOrders] = useState([]);
  const [pendingPayment, setPendingPayment] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showGuests, setShowGuests] = useState(false);
  const { getPaymentByTable } = usePayment();
  const { setTableGuests, setTableDelivery } = useTable();
  const [showDelivery, setShowDelivery] = useState(false);
  const { auth } = useAuth();
  const history = useHistory();

  // Una sola consulta con todos los pedidos abiertos de la mesa; los
  // contadores por estado se sacan de ahí.
  useEffect(() => {
    (async () => {
      const response = await getOrdersByTableApi(table.id, "", "", auth?.token);
      setOrders(Array.isArray(response) ? response : []);
      setLoaded(true);
    })();
  }, [reload]);

  const countStatus = (status) =>
    size(filter(orders, (order) => order.status === status));
  const pendingCount = countStatus(ORDER_STATUS.PENDING);
  // En WhatsApp / Venta rápida / Delivery todo sale en un lote: la mesa no se
  // pinta "listo" hasta que no quede nada por preparar.
  const readyCount =
    isBatchKind(table.kind) && countWaitingForArea(orders) > 0
      ? 0
      : countStatus(ORDER_STATUS.READY);
  const deliveredCount = countStatus(ORDER_STATUS.DELIVERED);
  const stateClasses = {
    busy: deliveredCount > 0,
    pending: pendingCount > 0,
    ready: readyCount > 0,
    "pending-payment": pendingPayment,
  };

  useEffect(() => {
    (async () => {
      const response = await getPaymentByTable(table.id);
      if (size(response) > 0) setPendingPayment(true);
      else setPendingPayment(false);
    })();
  }, [reload]);

  // Mesa libre del salón (sin pedidos ni cuenta): antes de entrar se pregunta
  // cuántas personas son (como Restaurant.pe) y se va directo a "Añadir
  // pedido". Las virtuales (venta rápida, WhatsApp, delivery) no preguntan.
  const isFree = loaded && size(orders) === 0 && !pendingPayment;
  const occupied = loaded && !isFree;
  useEffect(() => {
    if (onStatus && loaded) onStatus(table.id, { occupied, ready: readyCount > 0 });
  }, [loaded, occupied, readyCount]);
  const isFreeSalon = isFree && (!table.kind || table.kind === "SALON");
  // Delivery libre: primero canal y costo de envío ("Registra tu delivery").
  const isFreeDelivery = isFree && table.kind === "DELIVERY";

  const onClickTable = (event) => {
    if (isFreeSalon) {
      event.preventDefault();
      setShowGuests(true);
    } else if (isFreeDelivery) {
      event.preventDefault();
      setShowDelivery(true);
    }
  };

  const onConfirmDelivery = async (channel, fee) => {
    await setTableDelivery(table.id, channel, fee);
    setShowDelivery(false);
    cleanProductCartApi();
    history.push(`/admin/table/${table.id}/menu`);
  };

  const onConfirmGuests = async (guests, waiter) => {
    await setTableGuests(table.id, guests, waiter);
    setShowGuests(false);
    cleanProductCartApi();
    history.push(`/admin/table/${table.id}/menu`);
  };

  return (
    <>
      <Link
        className="table-admin"
        to={`/admin/table/${table.id}`}
        onClick={onClickTable}
      >
        {pendingCount > 0 ? (
          <Label circular color="orange">
            {pendingCount}
          </Label>
        ) : null}

        {/* Cocina ya lo marcó Listo: el mozo tiene que llevarlo. */}
        {readyCount > 0 && (
          <Label circular color="purple" className="table-admin__ready">
            {readyCount} listo{readyCount > 1 ? "s" : ""}
          </Label>
        )}

        {pendingPayment && (
          <Label circular color="orange" className="table-admin__payment">
            Cuenta
          </Label>
        )}

        {/* Mesas virtuales (Venta rápida / WhatsApp / Delivery): su propio ícono, mismos
          colores de estado que una mesa del salón. */}
        {table.kind && table.kind !== "SALON" ? (
          <Icon
            name={getTableKind(table.kind).icon}
            className={classNames("table-admin__virtual", stateClasses)}
          />
        ) : (
          <IcTable className={classNames(stateClasses)} />
        )}
        {/* Dentro de Pedidos la mesa ya está bajo su ambiente (pestaña o título):
            basta "Mesa 3". El nombre completo va en el cuadro de apertura. */}
        <p>{getTableShortName(table)}</p>
      </Link>

      {/* Fuera del <Link>: los clics dentro del modal (portal) igual "suben"
        por el árbol de React y dispararían la navegación del Link. */}
      <GuestsModal
        show={showGuests}
        title={getTablePlace(table)}
        onCancel={() => setShowGuests(false)}
        onConfirm={onConfirmGuests}
      />
      <DeliveryModal
        show={showDelivery}
        title={`Registra tu delivery · ${getTablePlace(table)}`}
        onCancel={() => setShowDelivery(false)}
        onConfirm={onConfirmDelivery}
      />
    </>
  );
}
